import { AsyncPipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import {
  enableProdMode,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { CropXCommonModule } from '@cropx-common/common.module';
import { WeatherStationDataState } from '@cropx-common/components/graphs/weather-station-data-fetcher/state/weather-station-data.state';
import { UserPermissionsViewModel } from '@cropx-common/components/user-permissions-table/service/UserPermissionsViewModel';
import { PreventDoubleSubmitInterceptor } from '@cropx-common/directives/disable-on-submit/prevent-double-submit-interceptor';
import { RequestTimingInterceptor } from '@cropx-common/monitoring/RequestTimingInterceptor';
import { RefDataState } from '@cropx-common/services/api-gateway/refdata.state';
import { UserState } from '@cropx-common/services/api-gateway/user.state';
import { LanguageService } from '@cropx-common/services/language/LanguageService';
import { PermissionsState } from '@cropx-common/services/permissions/permission.state';
import { MissingPayingEntityDeviceState } from '@cropx-common/state/subscriptions/subscriptions-missing-paying-entity.state';
import { SubscriptionsState } from '@cropx-common/state/subscriptions/subscriptions.state';
import { FieldWeatherStationState } from '@cropx-web/screens/field/field-weather-station/state/field-weather-station.state';
import { FieldState } from '@cropx-web/screens/field/state/field.state';
import { JohnDeereState } from '@cropx-web/screens/partner-connection/user-account/john-deere/state/john-deere.state';
import { WiseconnState } from '@cropx-web/screens/partner-connection/user-account/wiseconn/state/wiseconn.state';
import { SensorOverviewRainGaugeState } from '@cropx-web/screens/sensors-summary/sensor-overview/state/sensor-overview-rain-gauge.state';
import { AuthInterceptor } from '@cropx-web/services/auth/AuthInterceptor';
import { EffluentSystemListenerService } from '@cropx-web/services/shared/EffluentSystemListenerService';
import { OAuth2State } from '@cropx-web/state/oauth2.state';
import { ProductState } from '@cropx-web/state/product.state';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { withNgxsResetPlugin } from 'ngxs-reset-plugin';
import { ButtonModule } from 'primeng/button';
import { providePrimeNG } from 'primeng/config';
import { TagModule } from 'primeng/tag';

import { FilterState } from '../../../libs/common/src/lib/services/api-gateway/entity-filter.state';
import { AmplificationFactorState } from '../../../libs/common/src/lib/state/graph-settings/amplification-factor.state';
import { ProfileSumState } from '../../../libs/common/src/lib/state/graph-settings/profile-sum-settings.state';
import { RootZoneState } from '../../../libs/common/src/lib/state/graph-settings/root-zone-settings.state';
import { RainViewerState } from '../../../libs/common/src/lib/state/rainviewer.state';
import { AlarmState } from './../../../libs/common/src/lib/state/graph-settings/alarm-settings.state';
import { WaterHoldingCapacityState } from './../../../libs/common/src/lib/state/graph-settings/water-holding-capacity-settings.state';
import { CropxPreset } from './../../../libs/theming/cropx-theme';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { HttpLoaderFactory } from './app/app.module';
import { AddFieldState } from './app/components/add-new-field/state/add-field.state';
import { TargetMaxCanopyState } from './app/components/graphs/et-graphs/canopy-overview-graph/canopy-settings/canopy-settings.state';
import { WaterStressState } from './app/components/graphs/et-graphs/water-stress-overview-graph/water-stress-settings/water-stress-settings.state';
import { CropRecordingState } from './app/modules/crop-recording/state/crop-recording.state';
import { FarmScopeService } from './app/screens/farm/farm-scope.service';
import { FieldAgendaPageState } from './app/screens/field/field-agenda/state/field-agenda-page.state';
import { FieldMachineDataPageState } from './app/screens/field/field-agronomy/field-agronomy-machine-data/state/field-machine-data-page.state';
import { VraMapsPageState } from './app/screens/field/field-agronomy/field-agronomy-vra-maps/state/vra-maps-page.state';
import { CreateManagementZonesState } from './app/screens/field/field-agronomy/field-agronomy-vra-maps/vra-map-creation/create-management-zones/state/create-management-zones.state';
import { CreateVraMapPageState } from './app/screens/field/field-agronomy/field-agronomy-vra-maps/vra-map-creation/create-vra-map/state/create-vra-map-page.state';
import { CreateVriMapState } from './app/screens/field/field-agronomy/field-agronomy-vra-maps/vra-map-creation/create-vri/state/create-vri-map.state';
import { ManagementZonesEditorState } from './app/screens/field/field-agronomy/field-agronomy-vra-maps/vra-map-creation/management-zones-editor/state/management-zones-editor.state';
import { GenerateManagementZonesState } from './app/screens/field/field-agronomy/field-agronomy-vra-maps/vra-map-creation/state/generate-management-zones.state';
import { FieldDataPageState } from './app/screens/field/field-data/state/field-data-page.state';
import { FieldSatelliteState } from './app/screens/field/field-data/state/field-satellite.state';
import { FieldMapViewState } from './app/screens/field/field-map-view/state/field-map-view.state';
import { AgendaState } from './app/screens/field/state/agenda.state';
import { FieldAlarmState } from './app/screens/field/state/field-alarm-settings.state';
import { FieldDiseaseState } from './app/screens/field/state/field-disease.state';
import { FieldElevationState } from './app/screens/field/state/field-elevation.state';
import { FieldGraphState } from './app/screens/field/state/field-graph.state';
import { FieldMachineDataState } from './app/screens/field/state/field-machine-data.state';
import { FieldNutritionState } from './app/screens/field/state/field-nutrition.state';
import { FieldSensorsState } from './app/screens/field/state/field-sensors.state';
import { FieldSettingsState } from './app/screens/field/state/field-settings.state';
import { FieldSoilSettingsState } from './app/screens/field/state/field-soil-settings.state';
import { FieldSoilState } from './app/screens/field/state/field-soil.state';
import { FieldVraState } from './app/screens/field/state/field-vra.state';
import { FieldWeatherState } from './app/screens/field/state/field-weather.state';
import { FieldRecommendationState } from './app/screens/field/state/fieldRecommendation.state';
import { MachineDataPageState } from './app/screens/machine-data/state/machine-data-page.state';
import { OverviewState } from './app/screens/overview/overview-dashboard/state/overview.state';
import { AgcoState } from './app/screens/partner-connection/user-account/agco/state/agco.state';
import { AgriRouterState } from './app/screens/partner-connection/user-account/agrirouter/state/agrirouter.state';
import { ClaasState } from './app/screens/partner-connection/user-account/claas/state/claas.state';
import { CNHState } from './app/screens/partner-connection/user-account/cnh/state/cnh.state';
import { RaindancerState } from './app/screens/partner-connection/user-account/raindancer/state/raindancer.state';
import { SensorSummaryPageState } from './app/screens/sensors-summary/state/sensor-summary.state';
import { AppServicesModule } from './app/services/app-services.module';
import { MockInterceptor } from './app/services/http-interceptor/MockInterceptor';
import { CropxFarmsState } from './app/state/cropx-farms.state';
import { DacomState } from './app/state/dacom.state';
import { DevicesState } from './app/state/devices.state';
import { FarmScopeState } from './app/state/farm-scope.state';
import { FarmsState } from './app/state/farms.state';
import { FieldsState } from './app/state/fields.state';
import { MachineDataState } from './app/state/machine-data.state';
import { SatelliteImagesState } from './app/state/satellite-image.state';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
  const scriptEl = window.document.createElement('script');
  scriptEl.src = 'assets/scripts/hotjar.js';
  window.document.body.appendChild(scriptEl);
} else {
  const scriptEl = window.document.createElement('script');
  scriptEl.src = 'assets/scripts/hotjar-qa/hotjar.js';
  window.document.body.appendChild(scriptEl);
}

bootstrapApplication(AppComponent, {
  providers: [
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: CropxPreset,
        options: {
          darkModeSelector: '.dark-mode',
          cssLayer: {
            name: 'primeng',
            order:
              'tailwind-base, primeng, cx-extentions, cropx-base, tailwind-utilities',
          },
        },
      },
    }),
    importProvidersFrom(
      NgxsModule.forRoot(
        [
          UserState,
          RefDataState,
          DacomState,
          MachineDataState,
          AmplificationFactorState,
          MachineDataPageState,
          AgendaState,
          FieldAgendaPageState,
          FieldWeatherState,
          FieldDataPageState,
          ProductState,
          OverviewState,
          SatelliteImagesState,
          SubscriptionsState,
          FieldSatelliteState,
          FieldElevationState,
          FieldSatelliteState,
          PermissionsState,
          FieldState,
          FieldRecommendationState,
          FieldMachineDataState,
          FieldMapViewState,
          CropxFarmsState,
          FieldSettingsState,
          FieldSoilSettingsState,
          FieldSoilState,
          SensorOverviewRainGaugeState,
          FieldDiseaseState,
          FieldNutritionState,
          FieldsState,
          FieldGraphState,
          FieldVraState,
          FieldAlarmState,
          FarmsState,
          FarmScopeState,
          CropRecordingState,
          FieldSensorsState,
          CreateVraMapPageState,
          VraMapsPageState,
          FieldMachineDataPageState,
          MissingPayingEntityDeviceState,
          SensorSummaryPageState,
          WiseconnState,
          ProfileSumState,
          RootZoneState,
          WaterHoldingCapacityState,
          DevicesState,
          GenerateManagementZonesState,
          CreateManagementZonesState,
          ManagementZonesEditorState,
          CreateVriMapState,
          JohnDeereState,
          AddFieldState,
          FieldWeatherStationState,
          CNHState,
          WeatherStationDataState,
          RainViewerState,
          AgcoState,
          ClaasState,
          TargetMaxCanopyState,
          WaterStressState,
          AlarmState,
          AgriRouterState,
          RaindancerState,
          OAuth2State,
          FilterState,
        ],
        {
          developmentMode: !environment.production,
          selectorOptions: {
            injectContainerState: false,
            suppressErrors: false,
          },
        },
      ),
      NgxsStoragePluginModule.forRoot({
        keys: [
          'user.features',
          'user.token',
          'user.username',
          'user.localeId',
          'user.userId',
          'user.userUuid',
          'user.measurementMethod',
          'user.moistureScaleMethod',
          'user.clockSettingType',
          'user.dateFormat',
          'user.volumeMethod',
          `user.impersonating`,
        ],
      }),
      CropXCommonModule.forRoot(environment),
      AppRoutingModule,
      AppServicesModule,
      BrowserModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
      NgxsReduxDevtoolsPluginModule.forRoot(),
      ButtonModule,
      TagModule,
    ),
    withNgxsResetPlugin(),
    UserPermissionsViewModel,
    LanguageService,
    EffluentSystemListenerService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PreventDoubleSubmitInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestTimingInterceptor,
      multi: true,
    },
    ...(environment.mock
      ? [
          {
            provide: HTTP_INTERCEPTORS,
            useClass: MockInterceptor,
            multi: true,
          },
        ]
      : []),
    provideAppInitializer(() => {
      inject(FarmScopeService);
    }),
    provideAnimations(),
    // Hack to make AsyncPipe available in other pipes
    AsyncPipe,
  ],
}).catch((err) => console.error(err));
